/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';
import { InputData } from '../page-data/input-data';
import { ProblemStage } from '../common/page-info';
import { ProblemOption } from '../common/problem-option';
import { TagScenario } from '../common-components/tag-scenario';
import { Review } from '../common-components/review';

import {
  SFFProblemType,
  ITSEventType,
  ReviewClickEventData,
} from '../common/page-data';
import { getImageUrlFromScenario } from '../common/helpers';
import { PageComp } from './page-comp-props';

export const InputComp: PageComp = (props) => {
  const { isTeacherView, stage } = props;
  const pageData = props.pageData as InputData;

  const { options, type, scenario, skill } = pageData;

  const [selections, setSelections] = useState<string[]>(() => []);

  useEffect(() => {
    const options = pageData.options;
    const selections: string[] = [];

    options.forEach((option) => {
      if (option.isSelected) {
        selections.push(option.id);
      }
    });

    setSelections(selections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData]);

  const [hasReviewed, setHasReviewed] = useState(false);
  const onReviewed = (option: ProblemOption): void => {
    setHasReviewed(true);
    const data: ReviewClickEventData = {
      id: option.id,
    };
    pageData.recordEvent(ITSEventType.reviewClick, data);
    if (props.onReviewed) props.onReviewed(option);
  };

  const isMultiChoice = () => pageData.problemType === 'multiChoice';

  const updateSelection = (
    event: React.MouseEvent<HTMLElement>,
    newSel: string | string[] | null
  ) => {
    if (isMultiChoice()) {
      setSelections(newSel as string[]);

      options.forEach((v) => {
        const newSelState = (newSel as string[]).includes(v.id);
        if (v.isSelected !== newSelState) {
          pageData.recordEvent(ITSEventType.selectOption, {
            id: v.id,
            selected: v.isSelected,
            correctOption: v.isCorrectResponse,
            tags: v.tags,
          });
          if (props.onChange) props.onChange(v);
        }
        v.isSelected = newSelState;
      });
    } else {
      const newSelState = newSel as string | null;
      if (newSelState) setSelections([newSelState]);
      else setSelections([]);
      options.forEach((v) => {
        const vSelected = newSelState === v.id;
        if (v.isSelected !== vSelected) {
          pageData.recordEvent(ITSEventType.selectOption, {
            id: v.id,
            selected: vSelected,
            correctOption: v.isCorrectResponse,
            tags: v.tags,
          });
          if (props.onChange) props.onChange(v);
        }
        v.isSelected = vSelected;
      });
    }
  };

  const isReview = stage === ProblemStage.ShowResult;

  const generateType1Content = () => {
    const {
      name,
      content: { text: description },
    } = skill;
    const text = '**' + name + '**\n\n' + description;

    return <ReactMarkdown>{text}</ReactMarkdown>;
  };

  const generateContent = () => {
    return (
      <>
        <ReactMarkdown>
          {scenario?.content?.markdown || scenario?.content.text || ''}
        </ReactMarkdown>
        {type === SFFProblemType.Type3 ? (
          <p>
            <>
              <b>You could say to the patient: </b>
              {pageData.question}
            </>
          </p>
        ) : null}
      </>
    );
  };

  const isType1 = type === SFFProblemType.Type1;

  const imgUrl: string | undefined = !isType1
    ? getImageUrlFromScenario(scenario)
    : undefined;
  const background: string | undefined = !isType1
    ? scenario?.metadata.patient.background
    : undefined;
  const content: string | JSX.Element = !isType1
    ? generateContent()
    : generateType1Content();

  const indexOfOptionToReview = isReview
    ? options.findIndex((value) => !value.isAnsweredCorrectly)
    : -1;

  return (
    <div>
      <TagScenario
        imgUrl={imgUrl}
        background={background}
        content={content}
        description={pageData.descriptionElement}
      />
      <ToggleButtonGroup
        value={isMultiChoice() ? selections : selections[0]}
        exclusive={!isMultiChoice()}
        color='primary'
        orientation='vertical'
        onChange={updateSelection}
        sx={{
          width: '100%',
          mt: 2,
          '& .MuiToggleButtonGroup-grouped': {
            mb: 0.5,
          },
        }}
      >
        {options.map((option, index) => (
          <ToggleButton
            value={option.id}
            key={option.id}
            sx={{
              textTransform: 'none',
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              padding: 0,
              pointerEvents: isReview ? 'none' : 'auto',

              '& .MuiToggleButton-root': {
                borderRadius: '4px',
              },
            }}
          >
            <Box sx={{ p: 2, textAlign: 'start' }}>{option.text}</Box>
            {isReview && (
              <Review
                hasRequestReviewPrompt={!isTeacherView && indexOfOptionToReview === index && !hasReviewed}
                isCorrectResponse={option.isCorrectResponse}
                isSelected={option.isSelected}
                onReviewed={() => onReviewed(option)}
                reason={option?.reasonIncorrect}
              />
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
};
